import { Fragment } from 'react';
import { scaleBand, scaleLinear } from 'd3-scale';
import { stack } from 'd3-shape';
import { Box } from '@mentimeter/ragnar-ui/box';
import type {
  Accessor,
  ChoicesStaticOptions,
  SegmentedChoicesData,
  Visualization,
} from '../../types';
import { getElement } from '../../utils/non-empty-array';
import {
  LABELS_HEIGHT,
  LOW_OPACITY,
  MAX_BAR_WIDTH,
  MIN_BAR_HEIGHT,
  MIN_MARGIN_TOP,
  VALUE_TEXT_HEIGHT,
  VALUE_TEXT_MARGIN,
} from '../constants';
import { calculateBarSpacing } from '../utils/calculateBarSpacing';
import { Score, type CorrectnessIndicatorStatus } from '../Score';
import { BarsAxisLabels } from '../BarsAxisLabels';
import { SvgWithFont } from '../../utils/SvgWithFont';
import { SegmentsLegend } from './SegmentsLegend';
import { useSegmentHover } from './useSegmentHover';
import { getSegmentData, type SegmentData } from './getSegmentData';
import { SegmentRectangle } from './SegmentRectangle';
import {
  getBarPercentageGetter,
  getBarTotalById,
  getMinMaxIndexes,
  getSegmentPercentage,
} from './utils';
import { EmptyBars } from './EmptyBars';

const BAR_LABELS_HEIGHT = 100;

interface Segment {
  id: number | null;
  label: string;
  position: number;
}

const getSegmentOpacity = ({
  isSegmentHovered,
  isAnySegmentHovered,
  correctnessIndicatorStatus,
}: {
  isSegmentHovered: boolean;
  isAnySegmentHovered: boolean;
  correctnessIndicatorStatus: CorrectnessIndicatorStatus;
}) => {
  if (isAnySegmentHovered && !isSegmentHovered) {
    return LOW_OPACITY;
  }
  if (
    correctnessIndicatorStatus.isVisible &&
    !correctnessIndicatorStatus.isCorrect
  ) {
    return LOW_OPACITY;
  }
  return 1;
};

export const SegmentedBars = ({
  colors,
  textColor,
  data,
  staticOptions,
  sourceQuestionTitle,
  keyAccessor = (d: SegmentedChoicesData | ChoicesStaticOptions) =>
    String(d.id),
  labelAccessor = (d: ChoicesStaticOptions) => d.label,
  spacingPercentage,
  width: rootWidth,
  height: rootHeight,
  segments,
  segmentKeyAccessor = (s: Segment) => s.position,
  shouldShowScorePercentage,
  disableAnimations = false,
  shouldShowCorrect,
  correctAnswerAccessor = (d) => d.isMarkedCorrect,
  placeHolderColor,
  isScoreHidden,
  fontFamily,
}: Visualization<SegmentedChoicesData, ChoicesStaticOptions> & {
  spacingPercentage?: number;
  segments: Segment[];
  segmentKeyAccessor?: (d: Segment) => number;
  sourceQuestionTitle: string;
  shouldShowScorePercentage: false | { percentOf: number };
  shouldShowCorrect: boolean;
  correctAnswerAccessor?: Accessor<ChoicesStaticOptions, boolean>;
  placeHolderColor: string;
  isScoreHidden: boolean;
}) => {
  const { onSegmentHover, getIsSegmentHovered, isAnySegmentHovered } =
    useSegmentHover();

  const legendWidth = rootWidth * 0.3;
  const svgWidth = rootWidth - legendWidth;

  const calculatedSpacing =
    spacingPercentage ??
    calculateBarSpacing({
      containerWidth: svgWidth,
      numBars: staticOptions.length,
    });

  const maxBoundedWidth =
    (MAX_BAR_WIDTH * staticOptions.length) / (1 - calculatedSpacing);
  const boundedWidth = Math.min(svgWidth, maxBoundedWidth);
  const boundsLeftMargin = (svgWidth - boundedWidth) / 2;

  const discreteValues = staticOptions.map(keyAccessor);
  const discreteScale = scaleBand()
    .domain(discreteValues)
    .range([0, boundedWidth])
    .paddingInner(calculatedSpacing)
    .paddingOuter(0.00001); // having this at 0 makes the first bar disappear, I have no idea what I'm doing wrong

  const svgHeight = Math.max(rootHeight - BAR_LABELS_HEIGHT, 0);
  const boundedHeight = Math.max(svgHeight - MIN_MARGIN_TOP, 0);

  const stackSeries = stack().keys(
    segments.map(segmentKeyAccessor).map(String),
  );
  const stackData = getSegmentData(data, segments, segmentKeyAccessor);
  const series = stackSeries(stackData);

  const yMax = Math.max(
    ...series.map((segmentArray) =>
      Math.max(...segmentArray.map((dd) => dd[1])),
    ),
  );

  const yScale =
    yMax === 0
      ? () => 0 // linear scale doesn't work well with domain [0, 0], see https://stackoverflow.com/questions/70577576/d3-js-scalelinear-is-not-returning-the-correct-output-for-0-value
      : scaleLinear().domain([0, yMax]).range([0, boundedHeight]);

  if (svgHeight <= 0 || svgWidth <= 0) {
    return null;
  }

  const targetMinMaxIndexes = getMinMaxIndexes(data);

  const getCorrectnessIndicatorStatus = (
    barId: number,
  ): CorrectnessIndicatorStatus => {
    const staticOption = staticOptions.find(
      (option) => keyAccessor(option) === String(barId),
    );
    const isCorrect = Boolean(
      staticOption && correctAnswerAccessor(staticOption),
    );
    return {
      isVisible: shouldShowCorrect,
      isCorrect,
    };
  };

  const getBarScoreDisplayText = (barId: number) => {
    if (shouldShowScorePercentage) {
      const getBarPercentage = getBarPercentageGetter(
        data,
        shouldShowScorePercentage.percentOf,
      );
      return getBarPercentage(barId);
    } else {
      return getBarTotalById(barId, data).toString();
    }
  };

  return (
    <Box
      flexDirection="row"
      alignItems="center"
      height={rootHeight}
      aria-hidden
    >
      <Box flexDirection="column" width={svgWidth} height="100%">
        <SvgWithFont
          width={svgWidth}
          height={svgHeight}
          fontFamily={fontFamily}
        >
          <g
            data-testid="bounds"
            width={boundedWidth}
            height={boundedHeight}
            transform={`translate(${boundsLeftMargin}, ${MIN_MARGIN_TOP})`}
          >
            <g data-testid="segmented-bars">
              <EmptyBars
                data={data}
                discreteScale={discreteScale}
                boundedHeight={boundedHeight}
                textColor={textColor}
                placeHolderColor={placeHolderColor}
                shouldShowPercent={shouldShowScorePercentage}
                disableAnimations={disableAnimations}
                getCorrectnessIndicatorStatus={getCorrectnessIndicatorStatus}
              />
              {series.map((segmentArray, i) => {
                return (
                  <g key={`segmented-bar-${i}`} data-name={segmentArray.key}>
                    {segmentArray.map((group, j) => {
                      const barId = (group.data as SegmentData).id;
                      const x = discreteScale(String(barId)) || 0;
                      const rectangleY = boundedHeight - yScale(group[1]);

                      const color = getElement(
                        colors,
                        Number(segmentArray.key),
                      );

                      const isLowestRectangleInBar =
                        targetMinMaxIndexes[j]?.minIndex === i;
                      const isHighestRectangleInBar =
                        targetMinMaxIndexes[j]?.maxIndex === i;

                      const value = group[1] - group[0];
                      const height = yScale(value);

                      const correctnessIndicatorStatus =
                        getCorrectnessIndicatorStatus(barId);

                      return (
                        <Fragment key={`segment-fragment-${i}-${j}`}>
                          {value > 0 && (
                            <SegmentRectangle
                              value={
                                shouldShowScorePercentage
                                  ? value.toString()
                                  : getSegmentPercentage(value, barId, data)
                              }
                              x={x}
                              y={rectangleY}
                              height={height}
                              width={discreteScale.bandwidth()}
                              color={color}
                              isBottomRectangleInBar={isLowestRectangleInBar}
                              isTopRectangleInBar={isHighestRectangleInBar}
                              animateFromY={boundedHeight - height}
                              opacity={getSegmentOpacity({
                                isSegmentHovered: getIsSegmentHovered(
                                  Number(segmentArray.key),
                                ),
                                isAnySegmentHovered,
                                correctnessIndicatorStatus,
                              })}
                              disableAnimations={disableAnimations}
                              onMouseEnter={() =>
                                onSegmentHover(Number(segmentArray.key))
                              }
                              onMouseLeave={() => onSegmentHover(null)}
                            />
                          )}
                        </Fragment>
                      );
                    })}
                  </g>
                );
              })}
            </g>
          </g>
        </SvgWithFont>
        <Box
          data-testid="scores"
          width={boundedWidth}
          height={boundedHeight}
          top={MIN_MARGIN_TOP}
          left={boundsLeftMargin}
          position="absolute"
          // to enable SVG behind to be clickable
          style={{ pointerEvents: 'none' }}
        >
          {data.map((d) => {
            const barId = d.id;
            const barTotalScore = d.scores.reduce(
              (acc, score) => acc + score,
              0,
            );
            const yScaleValue =
              barTotalScore === 0 ? MIN_BAR_HEIGHT : yScale(barTotalScore);
            const rectangleY = boundedHeight - yScaleValue;
            const x = discreteScale(String(barId)) || 0;
            return (
              <Score
                key={`score-${barId}`}
                x={x}
                y={rectangleY - VALUE_TEXT_MARGIN}
                barWidth={discreteScale.bandwidth()}
                textColor={textColor}
                height={VALUE_TEXT_HEIGHT}
                scoreDisplayText={
                  isScoreHidden ? '' : getBarScoreDisplayText(barId)
                }
                correctnessIndicatorStatus={getCorrectnessIndicatorStatus(
                  barId,
                )}
                disableAnimations={disableAnimations}
              />
            );
          })}
        </Box>
        <BarsAxisLabels
          staticOptions={staticOptions}
          labelAccessor={labelAccessor}
          keyAccessor={keyAccessor}
          discreteScale={discreteScale}
          totalWidth={boundedWidth}
          totalHeight={LABELS_HEIGHT}
          marginLeft={boundsLeftMargin}
          marginTop={20}
          color={textColor}
          fontFamily={fontFamily}
        />
      </Box>
      <SegmentsLegend
        maxHeight={rootHeight}
        sourceQuestionTitle={sourceQuestionTitle}
        segments={segments.map((segment) => ({
          ...segment,
          color: getElement(colors, segmentKeyAccessor(segment)),
          opacity: getSegmentOpacity({
            isSegmentHovered: getIsSegmentHovered(segmentKeyAccessor(segment)),
            isAnySegmentHovered,
            correctnessIndicatorStatus: {
              isVisible: false, // correct answers don't relate to segments
              isCorrect: false,
            },
          }),
        }))}
        segmentKeyAccessor={segmentKeyAccessor}
        onSegmentHover={onSegmentHover}
      />
    </Box>
  );
};
